import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Modal, Form, Spinner, Table } from 'react-bootstrap';
import {
  PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import QuestionBankService from '../../_services/QuestionBankService';
import type {
  QuestionSearchResult,
  QuestionDetail,
  LookupData,
  SearchParams,
  QuestionFormData,
  AiGenerateParams,
} from '../../_services/QuestionBankService';
import QuestionTagSelector, { getSelectedTagIds } from '../quick-quiz-maker/QuestionTagSelector';
import type { TagSelection, AddedTag } from '../quick-quiz-maker/QuestionTagSelector';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CKEditorWithTools from './CKEditorWithTools';
import QuestionExtrasPanel from './QuestionExtrasPanel';
import { normalizeMathToEditable, restoreMathToStorage } from './mathmlToTex';
import GraphAuthoringCanvas from './GraphAuthoringCanvas';
import type { GraphConfig } from './GraphAuthoringCanvas';
// @ts-ignore — katex/contrib/auto-render has no type declarations
import renderMathInElement from 'katex/contrib/auto-render';
import 'katex/dist/katex.min.css';
import Config from '../../_config/config';
import './question-bank.css';

/**
 * Live KaTeX preview of editor content. The prebuilt CKEditor can't render math
 * inline, so it holds the editable TeX source (`\(...\)`) while this shows the
 * rendered result — same delimiters/engine as the view modal. Renders nothing
 * when the content has no math.
 */
const MathPreview: React.FC<{ html: string; label?: string }> = ({ html, label = 'Preview' }) => {
  const ref = useRef<HTMLDivElement>(null);
  const hasMath = !!html && /\\\(|\\\[/.test(html);
  useEffect(() => {
    const el = ref.current;
    if (!el || !hasMath) return;
    el.innerHTML = html;
    try {
      renderMathInElement(el, {
        delimiters: [
          { left: '\\(', right: '\\)', display: false },
          { left: '\\[', right: '\\]', display: true },
        ],
        throwOnError: false,
      });
    } catch { /* ignore render errors */ }
  }, [html, hasMath]);
  if (!hasMath) return null;
  return (
    <div style={{ marginTop: 6, padding: '6px 10px', background: '#f8f9ff', border: '1px solid #e6e8f5', borderRadius: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#8a8fb0', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>{label}</div>
      <div ref={ref} style={{ fontSize: '0.9rem', color: 'var(--text-strong)' }} />
    </div>
  );
};

/* ---- Custom S3 upload adapter with Bearer token ---- */
function S3UploadAdapterPlugin(editor: any) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => ({
    upload: () => loader.file.then((file: File) => new Promise((resolve: any, reject: any) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${Config.nodeSiteUrl}v1/question-bank/upload-image`, true);
      xhr.responseType = 'json';
      try {
        const cu = localStorage.getItem('currentUser');
        if (cu) { const u = JSON.parse(cu); if (u.token) xhr.setRequestHeader('Authorization', `Bearer ${u.token}`); }
      } catch { /* ignore */ }
      xhr.addEventListener('load', () => {
        if (!xhr.response || xhr.status >= 400) { reject(xhr.response?.error?.message || 'Upload failed'); return; }
        resolve({ default: xhr.response.url });
      });
      xhr.addEventListener('error', () => reject('Upload failed'));
      xhr.addEventListener('abort', () => reject('Upload aborted'));
      const data = new FormData();
      data.append('upload', file);
      xhr.send(data);
    })),
    abort: () => {},
  });
}

const IMAGE_CONFIG = {
  toolbar: [
    'imageStyle:alignLeft',
    'imageStyle:alignCenter',
    'imageStyle:alignRight',
  ],
};

const CKEDITOR_CONFIG_FULL = {
  extraPlugins: [S3UploadAdapterPlugin],
  toolbar: [
    'heading', '|', 'bold', 'italic', '|',
    'bulletedList', 'numberedList', '|',
    'insertTable', 'imageUpload', '|', 'undo', 'redo',
  ],
  image: IMAGE_CONFIG,
};

const CKEDITOR_CONFIG_COMPACT = {
  extraPlugins: [S3UploadAdapterPlugin],
  toolbar: ['bold', 'italic', '|', 'imageUpload', '|', 'undo', 'redo'],
  image: IMAGE_CONFIG,
};

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f472b6', '#6366f1', '#a3e635', '#fb923c', '#64748b', '#0ea5e9', '#e11d48', '#84cc16', '#d946ef'];

const PAGE_SIZE = 20;

/* Category IDs from question_category_master */
const CAT = {
  MCQ: 1,
  MTF: 2,
  TRUE_FALSE: 3,
  INTEGER_RANGE: 5,
  FILL_BLANKS: 7,
  FRACTION: 8,
  TEXT: 0,
  SCQ: 26,
  GRAPH: 27,
} as const;

/** Does this category use an answer text field? */
function catUsesAnswer(cat: number | null): boolean {
  return cat === CAT.TEXT || cat === CAT.FRACTION || cat === null;
}

/** Build default choices when switching categories in the edit form */
function defaultChoicesForCategory(cat: number | null) {
  if (cat === CAT.TRUE_FALSE) {
    return [
      { name: 'True', choiceString: 'A', correctFlag: 0, choiceGroup: 1 },
      { name: 'False', choiceString: 'B', correctFlag: 0, choiceGroup: 1 },
    ];
  }
  if (cat === CAT.SCQ || cat === CAT.MCQ) {
    return [
      { name: '', choiceString: 'A', correctFlag: 0, choiceGroup: 1 },
      { name: '', choiceString: 'B', correctFlag: 0, choiceGroup: 1 },
      { name: '', choiceString: 'C', correctFlag: 0, choiceGroup: 1 },
      { name: '', choiceString: 'D', correctFlag: 0, choiceGroup: 1 },
    ];
  }
  if (cat === CAT.MTF) {
    return [
      { name: '', choiceString: 'A', correctFlag: 0, choiceGroup: 1 },
      { name: '', choiceString: 'P', correctFlag: 0, choiceGroup: 2 },
      { name: '', choiceString: 'B', correctFlag: 0, choiceGroup: 1 },
      { name: '', choiceString: 'Q', correctFlag: 0, choiceGroup: 2 },
    ];
  }
  if (cat === CAT.FILL_BLANKS) {
    return [
      { name: '', choiceString: '', correctFlag: 1, choiceGroup: 3 },
    ];
  }
  if (cat === CAT.GRAPH) return [];
  return [];
}

const TAG_TYPE_COLORS: Record<number, string> = {
  5: '#e8f5e9',   // Course
  6: '#e3f2fd',   // Topic
  8: '#fff3e0',   // Difficulty
  9: '#fce4ec',   // Competency
  10: '#f3e5f5',  // Source
  12: '#e0f7fa',  // Other
};

/* ------------------------------------------------------------------ */
/*  Helper: strip HTML to plain text for table display                 */
/* ------------------------------------------------------------------ */
function stripHtml(html: string | null): string {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

/* ------------------------------------------------------------------ */
/*  Main Page Component                                                */
/* ------------------------------------------------------------------ */
const QuestionBankPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const initialUrlProcessed = useRef(false);

  // Embed mode: when ?embed=1 is set on first load (e.g. when this page is
  // loaded inside an iframe from QuizViewPanel's "Edit Question" modal), hide
  // the search/list chrome and only show the edit form modal. We capture this
  // once via useRef because openEditForm/closeFormModal call history.replace
  // and would strip the embed param from location.search on subsequent renders.
  // On save/close we postMessage to the parent so the host can close the iframe.
  const isEmbedRef = useRef(new URLSearchParams(location.search).get('embed') === '1');
  const isEmbed = isEmbedRef.current;
  // Original MathML captured (keyed by TeX) when a question is loaded for editing, so an
  // untouched formula is restored byte-identically on save instead of re-encoded. Rebuilt
  // on every edit-load (see handleEdit) and consumed by handleSave via restoreMathToStorage.
  const mathRestoreMapRef = useRef<Map<string, string>>(new Map());
  const notifyParentClose = () => {
    if (isEmbed && window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'qb-edit-close' }, '*');
    }
  };
  const notifyParentSaved = () => {
    if (isEmbed && window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'qb-edit-saved' }, '*');
    }
  };

  // Data
  const [questions, setQuestions] = useState<QuestionSearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [lookups, setLookups] = useState<LookupData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [topicId, setTopicId] = useState('');
  const [difficultyId, setDifficultyId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [selectedTagIds] = useState<number[]>([]);
  const [page, setPage] = useState(0);

  // Modals
  const [viewQuestion, setViewQuestion] = useState<QuestionDetail | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  // Inline tag editing inside the view modal's "Question Tags" card
  const [showAddTags, setShowAddTags] = useState(false);
  const [viewTagSelections, setViewTagSelections] = useState<Record<number, TagSelection>>({});
  const [viewAddedTags, setViewAddedTags] = useState<AddedTag[]>([]);
  const [savingViewTags, setSavingViewTags] = useState(false);
  const [viewTagError, setViewTagError] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Form state
  const [formData, setFormData] = useState<QuestionFormData>({
    stemStatement: '',
    name: '',
    mark: 0,
    questionCategory: null,
    topicMasterId: null,
    difficultyLevelId: null,
    questionTypeId: null,
    explanation: null,
    hint: null,
    answer: null,
    choices: [{ name: '', choiceString: 'A', correctFlag: 0, choiceGroup: 1 }],
    tagIds: [],
  });

  // Error/success
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // AI Generate
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiParams, setAiParams] = useState<AiGenerateParams>({
    topicId: 0, count: 5, questionTypeId: 1, difficultyLevelId: 1,
    questionCategoryId: 1, marks: 1,
  });
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState('');

  // AI Review
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<QuestionFormData[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [savingGenerated, setSavingGenerated] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ saved: 0, total: 0, errors: 0 });

  // Analytics
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');

  // Search error
  const [searchError, setSearchError] = useState('');

  // Tag selector state for edit form (QuestionTagSelector integration)
  const [tagSelections, setTagSelections] = useState<Record<number, TagSelection>>({});
  const [editAddedTags, setEditAddedTags] = useState<AddedTag[]>([]);

  // Existing tags for edit mode (removable pills)
  const [existingTags, setExistingTags] = useState<{ mappingId: number; tagId: number; tagName: string; tagTypeId: number; tagTypeName: string }[]>([]);

  // Context-based (comprehension) question state
  const [isContextBased, setIsContextBased] = useState(false);
  const [contextHtml, setContextHtml] = useState('');
  const [childQuestions, setChildQuestions] = useState<QuestionFormData[]>([]);

  // Graph question state
  const [graphConfig, setGraphConfig] = useState<GraphConfig | null>(null);

  /* ---- Load lookups on mount ---- */
  useEffect(() => {
    (async () => {
      const result = await QuestionBankService.getLookups();
      if (result.success && result.data) setLookups(result.data);
    })();
  }, []);

  /* ---- Analytics ---- */
  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsError('');
    const result = await QuestionBankService.getAnalytics();
    if (result.success && result.data) {
      setAnalyticsData(result.data);
    } else {
      setAnalyticsError(result.error || 'Failed to load analytics');
    }
    setAnalyticsLoading(false);
  }, []);

  const toggleAnalytics = () => {
    const willOpen = !analyticsOpen;
    setAnalyticsOpen(willOpen);
    if (willOpen && !analyticsData && !analyticsLoading) {
      loadAnalytics();
    }
  };

  /* ---- Search ---- */
  const doSearch = useCallback(async (pageNum = 0) => {
    setSearching(true);
    setSearchError('');
    const params: SearchParams = {
      q: searchText || undefined,
      topicId: topicId ? Number(topicId) : undefined,
      difficultyId: difficultyId ? Number(difficultyId) : undefined,
      typeId: typeId ? Number(typeId) : undefined,
      categoryId: categoryId ? Number(categoryId) : undefined,
      tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      limit: PAGE_SIZE,
      offset: pageNum * PAGE_SIZE,
    };
    const result = await QuestionBankService.search(params);
    if (result.success && result.data) {
      setQuestions(result.data.questions);
      setTotal(result.data.total);
      setSearchError('');
    } else {
      setSearchError(result.error || 'Search failed. Please try again.');
    }
    setSearching(false);
    setLoading(false);
  }, [searchText, topicId, difficultyId, typeId, categoryId, selectedTagIds]);

  useEffect(() => { doSearch(0); }, []); // initial load

  /* ---- Restore modal state from URL query params on mount ---- */
  useEffect(() => {
    if (initialUrlProcessed.current) return;
    initialUrlProcessed.current = true;
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    const id = params.get('id');
    if (id && mode === 'edit') {
      openEditForm(Number(id));
    } else if (id && mode === 'view') {
      handleView(Number(id));
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => { setPage(0); doSearch(0); };
  const handlePageChange = (newPage: number) => { setPage(newPage); doSearch(newPage); };

  /* ---- View detail ---- */
  const handleView = async (id: number) => {
    history.replace(`/question-bank?mode=view&id=${id}`);
    setLoadingDetail(true);
    setShowViewModal(true);
    const result = await QuestionBankService.getById(id);
    if (result.success && result.data) {
      setViewQuestion(result.data);
    }
    setLoadingDetail(false);
  };

  /* ---- Render KaTeX equations in view modal ---- */
  useEffect(() => {
    if (showViewModal && viewQuestion) {
      setTimeout(() => {
        const el = document.querySelector('.qb-view-container');
        if (el) {
          renderMathInElement(el as HTMLElement, {
            delimiters: [
              { left: '\\(', right: '\\)', display: false },
              { left: '\\[', right: '\\]', display: true },
            ],
            throwOnError: false,
          });
        }
      }, 100);
    }
  }, [showViewModal, viewQuestion]);

  const closeViewModal = () => {
    setShowViewModal(false);
    setShowAddTags(false);
    setViewTagSelections({});
    setViewAddedTags([]);
    setViewTagError('');
    history.replace('/question-bank');
  };

  /* ---- Inline tag editing in the view modal ---- */
  const handleSaveViewTags = async () => {
    if (!viewQuestion) return;
    const tagIds = getSelectedTagIds(viewTagSelections, viewAddedTags);
    if (tagIds.length === 0) { setShowAddTags(false); return; }
    setViewTagError('');
    setSavingViewTags(true);
    const res = await QuestionBankService.addTags(viewQuestion.Id, tagIds);
    setSavingViewTags(false);
    if (res.success) {
      setViewTagSelections({});
      setViewAddedTags([]);
      setShowAddTags(false);
      await handleView(viewQuestion.Id); // refresh pills
    } else {
      setViewTagError(res.error || 'Failed to add tags');
    }
  };

  const handleRemoveViewTag = async (mappingId: number) => {
    if (!viewQuestion) return;
    setViewTagError('');
    const res = await QuestionBankService.removeTag(viewQuestion.Id, mappingId);
    if (res.success) {
      await handleView(viewQuestion.Id);
    } else {
      setViewTagError(res.error || 'Failed to remove tag');
    }
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setErrorMsg('');
    if (isEmbed) {
      notifyParentClose();
      return;
    }
    history.replace('/question-bank');
  };

  /* ---- Open form for create / edit ---- */
  const openCreateForm = () => {
    setEditingId(null);
    // Fresh create: no original MathML to restore — new formulas convert TeX->MathML via KaTeX.
    mathRestoreMapRef.current = new Map();
    setFormData({
      stemStatement: '', name: '', mark: 0, questionCategory: null,
      topicMasterId: null, difficultyLevelId: null, questionTypeId: null,
      explanation: null, hint: null, answer: null,
      choices: [{ name: '', choiceString: 'A', correctFlag: 0, choiceGroup: 1 }],
      tagIds: [],
    });
    setTagSelections({});
    setEditAddedTags([]);
    setExistingTags([]);
    setIsContextBased(false);
    setContextHtml('');
    setChildQuestions([]);
    setShowFormModal(true);
  };

  const openEditForm = async (id: number) => {
    history.replace(`/question-bank?mode=edit&id=${id}${isEmbed ? '&embed=1' : ''}`);
    setLoadingDetail(true);
    setShowFormModal(true);
    setEditingId(id);
    const result = await QuestionBankService.getById(id);
    if (result.success && result.data) {
      const q = result.data;
      // Normalize any stored MathML to inline TeX so the prebuilt CKEditor (which strips
      // unrecognized MathML tags) keeps the formula intact instead of collapsing it to garbage.
      // Capture each formula's original MathML (keyed by TeX) so handleSave can restore
      // untouched formulas verbatim rather than re-encoding them.
      mathRestoreMapRef.current = new Map();
      const nm = (v: any) => (typeof v === 'string' ? normalizeMathToEditable(v, mathRestoreMapRef.current) : v);
      setFormData({
        name: nm(q.QuestionName || q.Name || q.StemStatement || ''), stemStatement: nm(q.QuestionStemStatement || q.StemStatement || ''), mark: q.Mark || 0,
        questionCategory: q.QuestionCategory, topicMasterId: q.TopicMasterId,
        difficultyLevelId: q.DifficultyLevelId, questionTypeId: q.QuestionTypeId,
        explanation: nm(q.Explanation), overallFeedback: nm(q.OverallFeedBack), hint: nm(q.Hint), answer: q.Answer,
        solutionLink: q.SolutionLink, instructions: nm(q.Instructions),
        answerRangeStart: q.AnswerRangeStart, answerRangeEnd: q.AnswerRangeEnd,
        choices: q.choices.length > 0
          ? q.choices.map(c => ({ name: nm(c.name), choiceString: c.choiceString, correctFlag: c.correctFlag, feedback: nm(c.feedback || ''), choiceGroup: c.choiceGroup }))
          : defaultChoicesForCategory(q.QuestionCategory),
        tagIds: q.tags.map(t => t.tagId),
      });

      // Pre-fill existing tags for removable pills in edit mode
      setExistingTags(q.tags.map(t => ({ mappingId: t.mappingId, tagId: t.tagId, tagName: t.tagName, tagTypeId: t.tagTypeId, tagTypeName: t.tagTypeName })));

      // Reset QuestionTagSelector (used for adding new tags)
      setTagSelections({});
      setEditAddedTags([]);

      // Pre-fill context if this is a context-based question
      if (q.context) {
        setIsContextBased(true);
        setContextHtml(normalizeMathToEditable(q.context.name || ''));
      } else {
        setIsContextBased(false);
        setContextHtml('');
      }
      setChildQuestions([]);

      // Restore graph config if this is a GRAPH question
      if (q.QuestionCategory === CAT.GRAPH && (q as any).GraphConfig) {
        const gc = typeof (q as any).GraphConfig === 'string' ? JSON.parse((q as any).GraphConfig) : (q as any).GraphConfig;
        setGraphConfig(gc);
      } else {
        setGraphConfig(null);
      }
    }
    setLoadingDetail(false);
  };

  /* ---- Save (create / update) ---- */
  const handleSave = async () => {
    // Validate: for context-based create, validate context + at least one child
    if (!editingId && isContextBased) {
      const ctxText = (contextHtml || '').replace(/<[^>]*>/g, '').trim();
      if (!ctxText) { setErrorMsg('Context is required for context-based questions'); return; }
      if (childQuestions.length === 0) { setErrorMsg('Add at least one question'); return; }
      for (let i = 0; i < childQuestions.length; i++) {
        const qText = (childQuestions[i].name || '').replace(/<[^>]*>/g, '').trim();
        if (!qText) { setErrorMsg(`Question ${i + 1} text is required`); return; }
      }
      setSaving(true);
      setErrorMsg('');
      const selectorTagIds = getSelectedTagIds(tagSelections, editAddedTags);
      const questionsWithTags = childQuestions.map(q => ({ ...q, tagIds: selectorTagIds.length > 0 ? selectorTagIds : q.tagIds }));
      const result = await QuestionBankService.createContextQuestions(contextHtml, questionsWithTags);
      setSaving(false);
      if (result.success) {
        closeFormModal();
        setSuccessMsg(`Context created with ${result.data?.questionIds?.length || 0} questions`);
        setTimeout(() => setSuccessMsg(''), 3000);
        doSearch(page);
      } else {
        setErrorMsg(result.error || 'Save failed');
      }
      return;
    }

    // Regular question save (create or edit)
    const questionText = (formData.name || '').replace(/<[^>]*>/g, '').trim();
    if (!questionText) {
      setErrorMsg('Question is required');
      return;
    }
    setSaving(true);
    setErrorMsg('');
    // Merge: existing tags (kept) + newly added from selector
    const existingTagIds = existingTags.map(t => t.tagId);
    const newTagIds = getSelectedTagIds(tagSelections, editAddedTags);
    const allTagIds = Array.from(new Set([...existingTagIds, ...newTagIds]));
    // Convert the editor's inline TeX back to MathML (the DB's canonical format) so every
    // viewer — iteach quiz views, the ilearn online test, PDFs — keeps rendering the formula.
    // Untouched formulas are restored to their original MathML; edited/new ones via KaTeX.
    const toStorage = (v: any) => (typeof v === 'string' ? restoreMathToStorage(v, mathRestoreMapRef.current) : v);
    const mergedForm: any = {
      ...formData,
      name: toStorage(formData.name),
      stemStatement: toStorage(formData.stemStatement),
      explanation: toStorage(formData.explanation),
      overallFeedback: toStorage(formData.overallFeedback),
      hint: toStorage(formData.hint),
      instructions: toStorage(formData.instructions),
      choices: Array.isArray(formData.choices)
        ? formData.choices.map((c: any) => ({ ...c, name: toStorage(c.name), feedback: toStorage(c.feedback) }))
        : formData.choices,
      tagIds: allTagIds.length > 0 ? allTagIds : formData.tagIds,
      // Pass contextHtml for edit so backend can update question_context
      contextHtml: isContextBased ? toStorage(contextHtml) : undefined,
      // Pass graphConfig for GRAPH category questions
      graphConfig: formData.questionCategory === CAT.GRAPH ? graphConfig : undefined,
    };
    const result = editingId
      ? await QuestionBankService.update(editingId, mergedForm)
      : await QuestionBankService.create(mergedForm);
    setSaving(false);
    if (result.success) {
      if (isEmbed) {
        notifyParentSaved();
        setShowFormModal(false);
        return;
      }
      closeFormModal();
      setSuccessMsg(editingId ? 'Question updated' : 'Question created');
      setTimeout(() => setSuccessMsg(''), 3000);
      doSearch(page);
    } else {
      setErrorMsg(result.error || 'Save failed');
    }
  };

  /* ---- Delete ---- */
  const handleDelete = async () => {
    if (!deletingId) return;
    setSaving(true);
    const result = await QuestionBankService.remove(deletingId);
    setSaving(false);
    if (result.success) {
      setShowDeleteModal(false);
      setSuccessMsg('Question deleted');
      setTimeout(() => setSuccessMsg(''), 3000);
      doSearch(page);
    } else {
      setErrorMsg(result.error || 'Delete failed');
    }
  };

  /* ---- Choice helpers ---- */
  const addChoice = () => {
    const letters = 'ABCDEFGHIJ';
    const next = letters[formData.choices?.length || 0] || '';
    setFormData(prev => ({
      ...prev,
      choices: [...(prev.choices || []), { name: '', choiceString: next, correctFlag: 0, choiceGroup: 1 }],
    }));
  };

  const removeChoice = (idx: number) => {
    setFormData(prev => ({
      ...prev,
      choices: (prev.choices || []).filter((_, i) => i !== idx),
    }));
  };

  const updateChoice = (idx: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      choices: (prev.choices || []).map((c, i) => i === idx ? { ...c, [field]: value } : c),
    }));
  };

  /* ---- Category change handler ---- */
  const handleCategoryChange = (newCat: number | null) => {
    const oldCat = formData.questionCategory;
    setFormData(prev => {
      const update: any = { ...prev, questionCategory: newCat };
      // Reset choices when switching between fundamentally different types
      if (newCat !== oldCat) {
        update.choices = defaultChoicesForCategory(newCat);
        if (!catUsesAnswer(newCat)) update.answer = null;
        if (newCat !== CAT.INTEGER_RANGE) {
          update.answerRangeStart = null;
          update.answerRangeEnd = null;
        }
      }
      return update;
    });
    // Initialize graph config when switching to GRAPH category
    if (newCat === CAT.GRAPH && oldCat !== CAT.GRAPH) {
      setGraphConfig({
        axes: { xMin: -10, xMax: 10, yMin: -10, yMax: 10, gridVisible: true },
        displayElements: [],
        expectedAnswer: [],
        tolerance: 0.5,
        interactionMode: 'plot-points',
        functions: [],
      });
    } else if (newCat !== CAT.GRAPH) {
      setGraphConfig(null);
    }
  };

  /* ---- MTF pair helpers ---- */
  const getMtfPairs = () => {
    const lefts = (formData.choices || []).filter(c => c.choiceGroup === 1);
    const rights = (formData.choices || []).filter(c => c.choiceGroup === 2);
    return lefts.map((l, i) => ({ left: l, right: rights[i] || null }));
  };

  const addMtfPair = () => {
    const lefts = (formData.choices || []).filter(c => c.choiceGroup === 1);
    const rights = (formData.choices || []).filter(c => c.choiceGroup === 2);
    const letters = 'ABCDEFGHIJ';
    const rLetters = 'PQRSTUVWXY';
    const newLeft = { name: '', choiceString: letters[lefts.length] || '', correctFlag: 0, choiceGroup: 1 };
    const newRight = { name: '', choiceString: rLetters[rights.length] || '', correctFlag: 0, choiceGroup: 2 };
    setFormData(prev => ({
      ...prev,
      choices: [...(prev.choices || []), newLeft, newRight],
    }));
  };

  const removeMtfPair = (pairIdx: number) => {
    const lefts = (formData.choices || []).filter(c => c.choiceGroup === 1);
    const rights = (formData.choices || []).filter(c => c.choiceGroup === 2);
    const newLefts = lefts.filter((_, i) => i !== pairIdx);
    const newRights = rights.filter((_, i) => i !== pairIdx);
    setFormData(prev => ({ ...prev, choices: [...newLefts, ...newRights] }));
  };

  const updateMtfItem = (group: number, idx: number, value: string) => {
    const items = (formData.choices || []).filter(c => c.choiceGroup === group);
    const others = (formData.choices || []).filter(c => c.choiceGroup !== group);
    const updated = items.map((c, i) => i === idx ? { ...c, name: value } : c);
    setFormData(prev => ({ ...prev, choices: [...others, ...updated] }));
  };

  /* ---- Fill in blanks helpers ---- */
  const getBlanks = () => (formData.choices || []).filter(c => c.choiceGroup >= 3).sort((a, b) => a.choiceGroup - b.choiceGroup);

  const addBlank = () => {
    const blanks = getBlanks();
    const nextGroup = blanks.length > 0 ? Math.max(...blanks.map(b => b.choiceGroup)) + 1 : 3;
    setFormData(prev => ({
      ...prev,
      choices: [...(prev.choices || []), { name: '', choiceString: '', correctFlag: 1, choiceGroup: nextGroup }],
    }));
  };

  const removeBlank = (blankIdx: number) => {
    const blanks = getBlanks();
    const target = blanks[blankIdx];
    if (!target) return;
    setFormData(prev => ({
      ...prev,
      choices: (prev.choices || []).filter(c => c.choiceGroup !== target.choiceGroup),
    }));
  };

  const updateBlank = (blankIdx: number, value: string) => {
    const blanks = getBlanks();
    const targetGroup = blanks[blankIdx]?.choiceGroup;
    if (targetGroup === undefined) return;
    setFormData(prev => ({
      ...prev,
      choices: (prev.choices || []).map(c => c.choiceGroup === targetGroup ? { ...c, name: value } : c),
    }));
  };

  /* ---- AI Generate handlers ---- */
  const handleAiGenerate = async () => {
    if (!aiParams.topicId) {
      setAiError('Please select a topic');
      return;
    }
    setGenerating(true);
    setAiError('');
    const result = await QuestionBankService.aiGenerate(aiParams);
    setGenerating(false);

    if (result.success && result.data) {
      setGeneratedQuestions(result.data.questions);
      setSelectedIndices(new Set(result.data.questions.map((_, i) => i)));
      setShowAiModal(false);
      setShowReviewModal(true);
      setExpandedIndex(0);
    } else {
      setAiError(result.error || 'Generation failed. Please try again.');
    }
  };

  const updateGeneratedQuestion = (idx: number, field: string, value: any) => {
    setGeneratedQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  };

  const updateGeneratedChoice = (idx: number, choiceIdx: number, field: string, value: any) => {
    setGeneratedQuestions(prev => prev.map((q, i) => {
      if (i !== idx) return q;
      return {
        ...q,
        choices: (q.choices || []).map((c, ci) => ci === choiceIdx ? { ...c, [field]: value } : c),
      };
    }));
  };

  const handleSaveGenerated = async () => {
    const toSave = generatedQuestions.filter((_, i) => selectedIndices.has(i));
    if (toSave.length === 0) return;

    setSavingGenerated(true);
    setSaveProgress({ saved: 0, total: toSave.length, errors: 0 });

    let saved = 0;
    let errors = 0;

    for (const q of toSave) {
      const result = await QuestionBankService.create(q);
      if (result.success) {
        saved++;
      } else {
        errors++;
      }
      setSaveProgress({ saved, total: toSave.length, errors });
    }

    setSavingGenerated(false);

    if (errors === 0) {
      setShowReviewModal(false);
      setSuccessMsg(`${saved} question${saved !== 1 ? 's' : ''} saved to question bank`);
      setTimeout(() => setSuccessMsg(''), 4000);
      doSearch(page);
    } else {
      setAiError(`Saved ${saved} questions, but ${errors} failed. You can retry the failed ones.`);
    }
  };

  /* ---- Pagination ---- */
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const pageStart = page * PAGE_SIZE + 1;
  const pageEnd = Math.min((page + 1) * PAGE_SIZE, total);

  /* ================================================================ */
  /*  RENDER                                                           */
  /* ================================================================ */
  return (
    <div className="question-bank-page">
      {/* Header — hidden in embed mode (iframe inside another modal) */}
      {!isEmbed && (
        <div className="page-header">
          <h4>Question Bank</h4>
          <div className="header-actions">
            <Button variant="primary" onClick={() => { setAiError(''); setShowAiModal(true); }}>AI Generate</Button>
            <Button variant="secondary" href="/question-bank/import">Import DOCX</Button>
            <Button variant="primary" onClick={openCreateForm}>+ New Question</Button>
          </div>
        </div>
      )}

      <div className="qb-content-area" style={isEmbed ? { display: 'none' } : undefined}>
      {successMsg && (
        <div style={{ background: 'linear-gradient(135deg, var(--status-success-bg) 0%, var(--status-success-bg) 100%)', color: 'var(--status-success-text)', padding: '12px 18px', borderRadius: 12, marginBottom: 16, fontSize: 13, fontWeight: 500, border: '1px solid #86efac', boxShadow: '0 2px 8px rgba(34,197,94,0.1)' }}>
          {successMsg}
        </div>
      )}

      {/* Analytics Panel */}
      <div className="qb-analytics-panel">
        <button className="qb-analytics-toggle" onClick={toggleAnalytics}>
          <span className="qb-analytics-toggle-icon">{analyticsOpen ? '\u25BE' : '\u25B8'}</span>
          <span>Analytics</span>
          {analyticsData && <span className="qb-analytics-total">{analyticsData.total.toLocaleString()} questions</span>}
        </button>
        {analyticsOpen && (
          <div className="qb-analytics-body">
            {analyticsLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spinner role="status" animation="border" size="sm" /> Loading analytics...</div>
            ) : analyticsError ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#c62828', fontSize: 13 }}>{analyticsError}</div>
            ) : analyticsData ? (
              <>
                {/* KPI row */}
                <div className="qb-analytics-kpi-row">
                  <div className="qb-analytics-kpi">
                    <div className="qb-analytics-kpi-value">{analyticsData.total.toLocaleString()}</div>
                    <div className="qb-analytics-kpi-label">Total Questions</div>
                  </div>
                  <div className="qb-analytics-kpi">
                    <div className="qb-analytics-kpi-value">{analyticsData.byType.length}</div>
                    <div className="qb-analytics-kpi-label">Question Types</div>
                  </div>
                  <div className="qb-analytics-kpi">
                    <div className="qb-analytics-kpi-value">{analyticsData.byCategory.length}</div>
                    <div className="qb-analytics-kpi-label">Categories</div>
                  </div>
                  <div className="qb-analytics-kpi">
                    <div className="qb-analytics-kpi-value">{analyticsData.byTag.length}</div>
                    <div className="qb-analytics-kpi-label">Tags Used</div>
                  </div>
                </div>

                {/* Charts */}
                <div className="qb-analytics-charts">
                  {/* By Type — Donut */}
                  {analyticsData.byType.length > 0 && (
                    <div className="qb-analytics-card">
                      <div className="qb-analytics-card-title">By Question Type</div>
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie
                            data={analyticsData.byType.map((d: any) => ({ name: d.name, value: Number(d.count) }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={85}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {analyticsData.byType.map((_: any, i: number) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: any, name: any) => [`${value} questions`, name]} />
                          <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* By Difficulty — Donut */}
                  {analyticsData.byDifficulty.length > 0 && (
                    <div className="qb-analytics-card">
                      <div className="qb-analytics-card-title">By Difficulty Level</div>
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie
                            data={analyticsData.byDifficulty.map((d: any) => ({ name: d.name, value: Number(d.count) }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={85}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {analyticsData.byDifficulty.map((_: any, i: number) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: any, name: any) => [`${value} questions`, name]} />
                          <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* By Category — Horizontal Bar */}
                  {analyticsData.byCategory.length > 0 && (
                    <div className="qb-analytics-card">
                      <div className="qb-analytics-card-title">By Category</div>
                      <ResponsiveContainer width="100%" height={Math.max(180, analyticsData.byCategory.length * 32)}>
                        <BarChart
                          data={analyticsData.byCategory.map((d: any) => ({
                            name: d.name.length > 18 ? d.name.substring(0, 16) + '...' : d.name,
                            fullName: d.name,
                            count: Number(d.count),
                          }))}
                          layout="vertical"
                          margin={{ left: 0, right: 20, top: 5, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                          <Tooltip
                            formatter={(value: any) => [`${value} questions`]}
                            labelFormatter={(label: any, payload: any) => payload?.[0]?.payload?.fullName || label}
                          />
                          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                            {analyticsData.byCategory.map((_: any, i: number) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* By Tag — Horizontal Bar */}
                  {analyticsData.byTag.length > 0 && (
                    <div className="qb-analytics-card">
                      <div className="qb-analytics-card-title">Top Tags</div>
                      <ResponsiveContainer width="100%" height={Math.max(180, analyticsData.byTag.length * 28)}>
                        <BarChart
                          data={analyticsData.byTag.map((d: any) => ({
                            name: d.name.length > 18 ? d.name.substring(0, 16) + '...' : d.name,
                            fullName: d.name,
                            typeName: d.typeName,
                            count: Number(d.count),
                          }))}
                          layout="vertical"
                          margin={{ left: 0, right: 20, top: 5, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                          <Tooltip
                            formatter={(value: any) => [`${value} questions`]}
                            labelFormatter={(label: any, payload: any) => {
                              const p = payload?.[0]?.payload;
                              return p ? `${p.fullName} (${p.typeName || 'Tag'})` : label;
                            }}
                          />
                          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                            {analyticsData.byTag.map((_: any, i: number) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                <div style={{ textAlign: 'right', marginTop: 8 }}>
                  <button className="action-btn" onClick={loadAnalytics} style={{ fontSize: 11 }}>Refresh</button>
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Search error */}
      {searchError && (
        <div className="qb-search-error">
          <span>{searchError}</span>
          <button onClick={() => setSearchError('')} style={{ background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer', opacity: 0.7, fontSize: 16 }}>&times;</button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-row">
        <div className="search-input">
          <label>Search</label>
          <Form.Control
            type="text"
            placeholder="Search by ID, name, or question text..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
        </div>
        {lookups && (
          <>
            <div className="filter-select">
              <label>Topic</label>
              <Form.Select value={topicId} onChange={e => setTopicId(e.target.value)}>
                <option value="">All Topics</option>
                {(lookups.tagTypes.find(tt => tt.id === 6)?.tags || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Form.Select>
            </div>
            <div className="filter-select">
              <label>Difficulty</label>
              <Form.Select value={difficultyId} onChange={e => setDifficultyId(e.target.value)}>
                <option value="">All Difficulty</option>
                {(lookups.tagTypes.find(tt => tt.id === 8)?.tags || []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Form.Select>
            </div>
            <div className="filter-select">
              <label>Type</label>
              <Form.Select value={typeId} onChange={e => setTypeId(e.target.value)}>
                <option value="">All Types</option>
                {lookups.types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Form.Select>
            </div>
            <div className="filter-select">
              <label>Category</label>
              <Form.Select value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                <option value="">All Categories</option>
                {lookups.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Form.Select>
            </div>
          </>
        )}
        <Button variant="primary" onClick={handleSearch} disabled={searching}>
          {searching ? <Spinner role="status" animation="border" size="sm" /> : 'Search'}
        </Button>
      </div>

      {/* Results table */}
      {loading ? (
        <div className="loading-container">Loading questions...</div>
      ) : questions.length === 0 ? (
        <div className="empty-state">No questions found. Try adjusting your search or filters.</div>
      ) : (
        <div className="results-table-container">
          <div className="table-responsive">
            <Table hover>
              <thead>
                <tr>
                  <th style={{ width: '60px', whiteSpace: 'nowrap' }}>ID</th>
                  <th>Question</th>
                  <th style={{ width: '100px', whiteSpace: 'nowrap' }}>Category</th>
                  <th style={{ width: '100px', whiteSpace: 'nowrap' }}>Difficulty</th>
                  <th style={{ width: '140px', whiteSpace: 'nowrap' }}>Topic</th>
                  <th style={{ width: '60px', whiteSpace: 'nowrap' }}>Marks</th>
                  <th style={{ width: '160px', whiteSpace: 'nowrap' }} className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {questions.map(q => (
                  <tr key={q.id}>
                    <td><span style={{ color: '#8b8fa3', fontWeight: 600, fontSize: 12 }}>{q.id}</span></td>
                    <td
                      className="question-text-cell"
                      title={stripHtml(q.stemStatement || q.name)}
                      onClick={() => handleView(q.id)}
                    >
                      {stripHtml(q.stemStatement || q.name)}
                    </td>
                    <td>{q.categoryName ? <span className="qb-table-badge">{q.categoryName}</span> : <span style={{ color: 'var(--text-faint)' }}>—</span>}</td>
                    <td>{q.difficultyName ? <span className="qb-table-badge qb-table-badge-difficulty">{q.difficultyName}</span> : <span style={{ color: 'var(--text-faint)' }}>—</span>}</td>
                    <td style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: 12 }}>
                      {q.topicName || <span style={{ color: 'var(--text-faint)' }}>—</span>}
                    </td>
                    <td>{q.mark != null ? <span className="qb-table-badge-marks">{q.mark}</span> : <span style={{ color: 'var(--text-faint)' }}>—</span>}</td>
                    <td className="text-end">
                      <div className="actions-cell" style={{ justifyContent: 'flex-end' }}>
                        <button className="action-btn" onClick={() => handleView(q.id)}>View</button>
                        <button className="action-btn" onClick={() => openEditForm(q.id)}>Edit</button>
                        <button className="action-btn delete" onClick={() => { setDeletingId(q.id); setShowDeleteModal(true); }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="pagination-row">
            <span>Showing {pageStart}–{pageEnd} of {total}</span>
            <div className="pagination-buttons">
              <button disabled={page === 0} onClick={() => handlePageChange(page - 1)}>Prev</button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const startPage = Math.max(0, Math.min(page - 2, totalPages - 5));
                const p = startPage + i;
                if (p >= totalPages) return null;
                return (
                  <button key={p} className={p === page ? 'active' : ''} onClick={() => handlePageChange(p)}>
                    {p + 1}
                  </button>
                );
              })}
              <button disabled={page >= totalPages - 1} onClick={() => handlePageChange(page + 1)}>Next</button>
            </div>
          </div>
        </div>
      )}
      </div>{/* end qb-content-area */}

      {/* ============ View Detail Modal ============ */}
      <Modal show={showViewModal} onHide={closeViewModal} dialogClassName="qb-fullscreen-modal">
        <Modal.Header closeButton style={{ background: 'var(--gradient-brand)', color: 'var(--text-on-brand)', borderBottom: 'none' }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 600 }}>
            Question #{viewQuestion?.Id} — {viewQuestion?.categoryName || 'View'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#f5f6fa', overflowY: 'auto' }}>
          {loadingDetail ? (
            <div style={{ textAlign: 'center', padding: 60 }}><Spinner role="status" animation="border" size="sm" /> Loading question...</div>
          ) : viewQuestion ? (
            <div className="qb-view-container">
              {/* ---- Left: Main content ---- */}
              <div className="qb-view-main">
                {/* Context / Passage (from question_context table via contextId) */}
                {viewQuestion.context && (
                  <div className="qb-view-card" style={{ borderLeft: '4px solid var(--status-success)', background: 'linear-gradient(135deg, var(--status-success-bg) 0%, var(--surface-card) 100%)' }}>
                    <div className="qb-view-card-label" style={{ color: 'var(--status-success)' }}>Passage / Context</div>
                    <div className="question-html-content" dangerouslySetInnerHTML={{ __html: viewQuestion.context.name || '' }} />
                  </div>
                )}

                {/* Context (from StemStatement column) */}
                {(viewQuestion.QuestionStemStatement || viewQuestion.StemStatement) && (
                  <div className="qb-view-card qb-card-context">
                    <div className="qb-view-card-label">Context</div>
                    <div
                      className="question-html-content"
                      dangerouslySetInnerHTML={{ __html: viewQuestion.QuestionStemStatement || viewQuestion.StemStatement || '' }}
                    />
                  </div>
                )}

                {/* Question (from Name column) */}
                <div className="qb-view-card qb-card-question">
                  <div className="qb-view-card-label">Question</div>
                  <div
                    className="question-html-content"
                    dangerouslySetInnerHTML={{ __html: viewQuestion.QuestionName || viewQuestion.Name || '' }}
                  />
                </div>

                {/* ---- Category-specific answer section ---- */}
                {(() => {
                  const cat = viewQuestion.QuestionCategory;
                  const regularChoices = viewQuestion.choices.filter(c => c.choiceGroup === 1);
                  const rightChoices = viewQuestion.choices.filter(c => c.choiceGroup === 2);
                  const blankChoices = viewQuestion.choices.filter(c => c.choiceGroup >= 3).sort((a, b) => a.choiceGroup - b.choiceGroup);

                  return (
                    <>
                      {/* SCQ / MCQ / True-False */}
                      {(cat === CAT.SCQ || cat === CAT.MCQ || cat === CAT.TRUE_FALSE || (!cat && regularChoices.length > 0)) && regularChoices.length > 0 && (
                        <div className="qb-view-card qb-card-choices">
                          <div className="qb-view-card-label">
                            {cat === CAT.TRUE_FALSE ? 'Options' : 'Choices'}
                            {cat === CAT.SCQ && <span className="qb-view-badge-info">Single Correct</span>}
                            {cat === CAT.MCQ && <span className="qb-view-badge-info">Multiple Correct</span>}
                          </div>
                          <ul className="choices-list">
                            {regularChoices.map(c => (
                              <li key={c.id || c.choiceString} className={c.correctFlag ? 'correct' : ''}>
                                <span className="choice-letter">{c.choiceString}</span>
                                <span dangerouslySetInnerHTML={{ __html: c.name }} />
                                {c.correctFlag ? <span className="qb-view-correct-badge">&#10003; CORRECT</span> : null}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Match the Following */}
                      {cat === CAT.MTF && (regularChoices.length > 0 || rightChoices.length > 0) && (
                        <div className="qb-view-card">
                          <div className="qb-view-card-label">Match the Following</div>
                          <Table size="sm" bordered className="qb-mtf-table">
                            <thead><tr><th>Left</th><th>Right</th></tr></thead>
                            <tbody>
                              {regularChoices.map((left, i) => (
                                <tr key={i}>
                                  <td><strong>{left.choiceString}.</strong> <span dangerouslySetInnerHTML={{ __html: left.name }} /></td>
                                  <td>{rightChoices[i] ? <><strong>{rightChoices[i].choiceString}.</strong> <span dangerouslySetInnerHTML={{ __html: rightChoices[i].name }} /></> : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}

                      {/* Fill in Blanks */}
                      {cat === CAT.FILL_BLANKS && blankChoices.length > 0 && (
                        <div className="qb-view-card">
                          <div className="qb-view-card-label">Blanks</div>
                          <Table size="sm" bordered className="qb-blanks-table">
                            <thead><tr><th style={{ width: 80 }}>Blank #</th><th>Answer</th></tr></thead>
                            <tbody>
                              {blankChoices.map((blank, i) => (
                                <tr key={i}><td style={{ textAlign: 'center', fontWeight: 600 }}>{i + 1}</td><td dangerouslySetInnerHTML={{ __html: blank.name }} /></tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}

                      {/* Integer Range */}
                      {cat === CAT.INTEGER_RANGE && (
                        <div className="qb-view-card">
                          <div className="qb-view-card-label">Answer Range</div>
                          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-strong)' }}>
                            {viewQuestion.AnswerRangeStart ?? '—'} &nbsp;to&nbsp; {viewQuestion.AnswerRangeEnd ?? '—'}
                          </div>
                        </div>
                      )}

                      {/* Text / Fraction / Integer */}
                      {viewQuestion.Answer && cat !== CAT.SCQ && cat !== CAT.MCQ && cat !== CAT.TRUE_FALSE && cat !== CAT.MTF && cat !== CAT.FILL_BLANKS && (
                        <div className="qb-view-card qb-card-answer">
                          <div className="qb-view-card-label">Answer</div>
                          <div className="question-html-content" dangerouslySetInnerHTML={{ __html: viewQuestion.Answer }} />
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Explanation (from OverallFeedBack column) */}
                {(viewQuestion.OverallFeedBack || viewQuestion.Explanation) && (
                  <div className="qb-view-card qb-card-explanation">
                    <div className="qb-view-card-label">Explanation</div>
                    <div className="question-html-content" dangerouslySetInnerHTML={{ __html: viewQuestion.OverallFeedBack || viewQuestion.Explanation || '' }} />
                  </div>
                )}

                {/* Hint */}
                {viewQuestion.Hint && (
                  <div className="qb-view-card">
                    <div className="qb-view-card-label">Hint</div>
                    <div style={{ fontSize: 13, color: 'var(--text-body)' }}>{viewQuestion.Hint}</div>
                  </div>
                )}

                {/* Solution Link */}
                {viewQuestion.SolutionLink && (
                  <div className="qb-view-card">
                    <div className="qb-view-card-label">Solution Link</div>
                    <a href={viewQuestion.SolutionLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>{viewQuestion.SolutionLink}</a>
                  </div>
                )}

                {/* Instructions */}
                {viewQuestion.Instructions && (
                  <div className="qb-view-card">
                    <div className="qb-view-card-label">Instructions</div>
                    <div className="question-html-content" dangerouslySetInnerHTML={{ __html: viewQuestion.Instructions }} />
                  </div>
                )}

                {/* Tags card — always show all tag types */}
                <div className="qb-view-card qb-card-tags">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div className="qb-view-card-label" style={{ marginBottom: 0 }}>Question Tags</div>
                    {lookups && lookups.tagTypes && lookups.tagTypes.length > 0 && (
                      <Button variant="primary" size="sm" style={{ padding: '2px 10px', fontSize: 12 }} onClick={() => setShowAddTags(true)}>
                        + Add Tags
                      </Button>
                    )}
                  </div>
                  {viewTagError && (
                    <div style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger)', border: '1px solid var(--status-danger-border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
                      {viewTagError}
                    </div>
                  )}
                  {(() => {
                    // Build a map of tagTypeId -> tags from the question
                    const tagsByType: Record<number, typeof viewQuestion.tags> = {};
                    viewQuestion.tags.forEach(t => {
                      if (!tagsByType[t.tagTypeId]) tagsByType[t.tagTypeId] = [];
                      tagsByType[t.tagTypeId].push(t);
                    });

                    // Use lookups.tagTypes to show all types; fall back to grouped question tags if lookups unavailable
                    const tagTypes = lookups?.tagTypes;
                    if (tagTypes && tagTypes.length > 0) {
                      return tagTypes.map(tt => (
                        <div key={tt.id} className="qb-view-tag-group">
                          <div className="qb-view-tag-type">{tt.name}</div>
                          <div className="qb-view-tag-list">
                            {tagsByType[tt.id] && tagsByType[tt.id].length > 0
                              ? tagsByType[tt.id].map(t => (
                                  <span key={t.mappingId} className="qb-view-tag-pill" style={{ background: TAG_TYPE_COLORS[tt.id] || '#e3f2fd' }}>
                                    {t.tagName}
                                    <button className="qb-edit-tag-remove" onClick={() => handleRemoveViewTag(t.mappingId)} title="Remove tag">&times;</button>
                                  </span>
                                ))
                              : <span style={{ fontSize: 12, color: '#b0b8c9', fontStyle: 'italic' }}>—</span>
                            }
                          </div>
                        </div>
                      ));
                    }
                    // Fallback: only show types that have tags
                    const grouped: Record<string, { typeId: number; tags: typeof viewQuestion.tags }> = {};
                    viewQuestion.tags.forEach(t => {
                      const key = t.tagTypeName || 'Other';
                      if (!grouped[key]) grouped[key] = { typeId: t.tagTypeId, tags: [] };
                      grouped[key].tags.push(t);
                    });
                    return Object.entries(grouped).map(([typeName, { typeId, tags }]) => (
                      <div key={typeName} className="qb-view-tag-group">
                        <div className="qb-view-tag-type">{typeName}</div>
                        <div className="qb-view-tag-list">
                          {tags.map(t => (
                            <span key={t.mappingId} className="qb-view-tag-pill" style={{ background: TAG_TYPE_COLORS[typeId] || '#e3f2fd' }}>
                              {t.tagName}
                              <button className="qb-edit-tag-remove" onClick={() => handleRemoveViewTag(t.mappingId)} title="Remove tag">&times;</button>
                            </span>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}

                  {/* Add Tags modal popup */}
                  <Modal
                    show={showAddTags}
                    onHide={() => { setShowAddTags(false); setViewTagSelections({}); setViewAddedTags([]); }}
                    size="lg" centered
                  >
                    <Modal.Header closeButton>
                      <Modal.Title style={{ fontSize: 16 }}>Add Tags</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      {viewTagError && (
                        <div style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger)', border: '1px solid var(--status-danger-border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
                          {viewTagError}
                        </div>
                      )}
                      {lookups && lookups.tagTypes && lookups.tagTypes.length > 0 && (
                        <QuestionTagSelector
                          fullWidth
                          tagTypes={lookups.tagTypes}
                          tagSelections={viewTagSelections}
                          onTagSelectionsChange={setViewTagSelections}
                          addedTags={viewAddedTags}
                          onAddedTagsChange={setViewAddedTags}
                        />
                      )}
                    </Modal.Body>
                    <Modal.Footer>
                      <Button variant="secondary" onClick={() => { setShowAddTags(false); setViewTagSelections({}); setViewAddedTags([]); }}>Cancel</Button>
                      <Button variant="primary" onClick={handleSaveViewTags} disabled={savingViewTags}>
                        {savingViewTags ? 'Saving...' : 'Save'}
                      </Button>
                    </Modal.Footer>
                  </Modal>
                </div>

                {/* Question Doc, Solution Video, Rubric, Reviews (legacy parity) */}
                <QuestionExtrasPanel
                  questionId={viewQuestion.Id}
                  solutionLink={(viewQuestion as any).SolutionLink}
                  onReplaced={() => handleView(viewQuestion.Id)}
                />
              </div>

              {/* ---- Right: Sidebar with meta & tags ---- */}
              <div className="qb-view-sidebar">
                {/* Meta info card */}
                <div className="qb-view-card qb-card-info">
                  <div className="qb-view-card-label" style={{ marginBottom: 12 }}>Question Info</div>
                  <div className="qb-view-meta-row">
                    <span className="qb-view-meta-label">ID</span>
                    <span className="qb-view-meta-value">{viewQuestion.Id}</span>
                  </div>
                  <div className="qb-view-meta-row">
                    <span className="qb-view-meta-label">Category</span>
                    <span className="qb-view-meta-value">{viewQuestion.categoryName || '—'}</span>
                  </div>
                  <div className="qb-view-meta-row">
                    <span className="qb-view-meta-label">Type</span>
                    <span className="qb-view-meta-value">{viewQuestion.questionTypeName || '—'}</span>
                  </div>
                  <div className="qb-view-meta-row">
                    <span className="qb-view-meta-label">Marks</span>
                    <span className="qb-view-meta-value" style={{ fontWeight: 700, fontSize: 15 }}>{viewQuestion.Mark ?? '—'}</span>
                  </div>
                </div>

              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid var(--border-default)' }}>
          <Button variant="outline-secondary" onClick={closeViewModal} style={{ margin: '2px' }}>Close</Button>
          {viewQuestion && (
            <Button variant="primary" onClick={() => { closeViewModal(); openEditForm(viewQuestion.Id); }} style={{ margin: '2px' }}>
              Edit Question
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* ============ Create/Edit Form Modal ============ */}
      <Modal show={showFormModal} onHide={closeFormModal} dialogClassName="qb-fullscreen-modal qb-form-modal">
        <Modal.Header closeButton style={{ background: editingId ? 'var(--gradient-brand)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'var(--text-on-brand)', borderBottom: 'none' }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 600 }}>{editingId ? `Edit Question #${editingId}` : 'New Question'}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflowY: 'auto', background: '#f5f6fa' }}>
          {loadingDetail ? (
            <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
          ) : (
            <div className="qb-form" style={{ maxWidth: 960, margin: '0 auto' }}>
              {errorMsg && (
                <div className="qb-form-error">{errorMsg}</div>
              )}

              {/* Row 1: Category + Type + Marks */}
              <div className="qb-meta-grid" style={{ gridTemplateColumns: '1fr 1fr 120px' }}>
                {lookups && (
                  <>
                    <div>
                      <label className="qb-label">Category <span className="text-danger">*</span></label>
                      <Form.Select size="sm" value={formData.questionCategory ?? ''} onChange={e => handleCategoryChange(e.target.value ? Number(e.target.value) : null)}>
                        <option value="">— Select Category —</option>
                        {lookups.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </Form.Select>
                    </div>
                    <div>
                      <label className="qb-label">Type</label>
                      <Form.Select size="sm" value={formData.questionTypeId || ''} onChange={e => setFormData(prev => ({ ...prev, questionTypeId: e.target.value ? Number(e.target.value) : null }))}>
                        <option value="">— None —</option>
                        {lookups.types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </Form.Select>
                    </div>
                  </>
                )}
                <div>
                  <label className="qb-label">Marks</label>
                  <Form.Control
                    type="number"
                    value={formData.mark || ''}
                    onChange={e => setFormData(prev => ({ ...prev, mark: Number(e.target.value) }))}
                    size="sm"
                  />
                </div>
              </div>

              {/* Symbols tip */}
              <div className="qb-symbols-tip">
                <span className="qb-symbols-tip-icon" title="Tip: Insert symbols">&#9432;</span>
                <span>To insert special characters &amp; symbols (±, ×, ÷, √, π, Σ, ∞), press <strong>Win + .</strong> (Windows) or <strong>Ctrl + Cmd + Space</strong> (Mac) to open the system character picker.</span>
              </div>

              {/* Context-based toggle (only for new questions without existing context) */}
              {!editingId && (
                <div className="qb-form-section" style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'linear-gradient(135deg, #f0f4ff 0%, #faf8ff 100%)', borderLeft: '4px solid var(--brand-accent)' }}>
                  <Form.Check
                    type="switch"
                    id="context-based-toggle"
                    label={<span style={{ fontWeight: 600, fontSize: 13, color: '#4c1d95' }}>Context-based question (Comprehension)</span>}
                    checked={isContextBased}
                    onChange={e => {
                      setIsContextBased(e.target.checked);
                      if (e.target.checked && childQuestions.length === 0) {
                        setChildQuestions([{ name: '', stemStatement: '', mark: 0, questionCategory: null, choices: defaultChoicesForCategory(null), tagIds: [] }]);
                      }
                    }}
                  />
                </div>
              )}

              {/* Context CKEditor — shown BEFORE question when editing a context-based question OR creating one */}
              {(isContextBased || (editingId && contextHtml)) && (
                <div className="qb-form-section qb-edit-context" style={{ borderLeft: '4px solid var(--status-success)', background: 'linear-gradient(135deg, var(--status-success-bg) 0%, var(--surface-card) 100%)' }}>
                  <label className="qb-label" style={{ color: 'var(--status-success)' }}>Context / Passage <span className="text-danger">*</span></label>
                  <CKEditorWithTools
                    editorKey={`q-ctx-${editingId || 'new'}-${loadingDetail}`}
                    data={isContextBased && !editingId ? contextHtml : (editingId ? contextHtml : formData.stemStatement || '')}
                    onChange={(data: string) => {
                      if (isContextBased && !editingId) {
                        setContextHtml(data);
                      } else if (editingId && contextHtml !== undefined) {
                        setContextHtml(data);
                      } else {
                        setFormData(prev => ({ ...prev, stemStatement: data }));
                      }
                    }}
                    config={CKEDITOR_CONFIG_FULL}
                    placeholder="Enter the shared passage/context text"
                    showChartButton
                  />
                </div>
              )}

              {/* Question (Name column) — CKEditor with equation/chart tools */}
              <div className="qb-form-section qb-edit-question">
                <label className="qb-label">Question <span className="text-danger">*</span></label>
                <CKEditorWithTools
                  editorKey={`q-name-${editingId || 'new'}-${loadingDetail}`}
                  data={formData.name || ''}
                  onChange={(data: string) => {
                    setFormData(prev => ({ ...prev, name: data }));
                  }}
                  config={CKEDITOR_CONFIG_FULL}
                  placeholder="Enter question"
                  showChartButton
                />
                <MathPreview html={formData.name || ''} label="Question preview" />
              </div>

              {/* ---- Child questions for context-based CREATE mode ---- */}
              {!editingId && isContextBased && (
                <div className="qb-form-section" style={{ borderLeft: '4px solid var(--status-success)', background: 'linear-gradient(135deg, var(--status-success-bg) 0%, var(--surface-card) 100%)' }}>
                  <label className="qb-label" style={{ fontSize: 13, marginBottom: 16 }}>
                    Questions under this Context ({childQuestions.length})
                  </label>

                  {childQuestions.map((child, ci) => (
                    <div key={ci} style={{ background: 'var(--surface-card)', border: '1px solid #ebeef5', borderRadius: 12, padding: 18, marginBottom: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-body)' }}>Question {ci + 1}</span>
                        {childQuestions.length > 1 && (
                          <button
                            className="qb-choice-remove"
                            style={{ fontSize: 16, color: 'var(--status-danger)' }}
                            onClick={() => setChildQuestions(prev => prev.filter((_, i) => i !== ci))}
                            title="Remove question"
                          >&times; Remove</button>
                        )}
                      </div>

                      {/* Child question text */}
                      <div style={{ marginBottom: 10 }}>
                        <label className="qb-label">Question <span className="text-danger">*</span></label>
                        <CKEditor
                          editor={ClassicEditor}
                          data={child.name || ''}
                          onChange={(_event: any, editor: any) => {
                            const val = editor.getData();
                            setChildQuestions(prev => prev.map((q, i) => i === ci ? { ...q, name: val } : q));
                          }}
                          config={{ ...CKEDITOR_CONFIG_COMPACT, placeholder: `Question ${ci + 1} text` }}
                        />
                      </div>

                      {/* Child: Category + Marks */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 10, marginBottom: 10 }}>
                        {lookups && (
                          <div>
                            <label className="qb-label">Category</label>
                            <Form.Select size="sm" value={child.questionCategory ?? ''}
                              onChange={e => {
                                const newCat = e.target.value ? Number(e.target.value) : null;
                                setChildQuestions(prev => prev.map((q, i) => i === ci ? { ...q, questionCategory: newCat, choices: defaultChoicesForCategory(newCat) } : q));
                              }}>
                              <option value="">— Select —</option>
                              {lookups.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </Form.Select>
                          </div>
                        )}
                        <div>
                          <label className="qb-label">Marks</label>
                          <Form.Control size="sm" type="number" value={child.mark || ''}
                            onChange={e => setChildQuestions(prev => prev.map((q, i) => i === ci ? { ...q, mark: Number(e.target.value) } : q))} />
                        </div>
                      </div>

                      {/* Child: Choices for SCQ/MCQ/TF */}
                      {(child.questionCategory === CAT.SCQ || child.questionCategory === CAT.MCQ || child.questionCategory === CAT.TRUE_FALSE) && (
                        <div style={{ marginBottom: 10 }}>
                          <label className="qb-label">Choices</label>
                          <div className="qb-choices-list">
                            {(child.choices || []).filter(c => c.choiceGroup === 1).map((choice, idx) => (
                              <div className="qb-choice-row" key={idx}>
                                <span className="qb-choice-letter">{choice.choiceString}</span>
                                <input className="form-control form-control-sm qb-choice-text" value={choice.name}
                                  onChange={e => {
                                    setChildQuestions(prev => prev.map((q, i) => {
                                      if (i !== ci) return q;
                                      const newChoices = (q.choices || []).map((c, j) => j === idx ? { ...c, name: e.target.value } : c);
                                      return { ...q, choices: newChoices };
                                    }));
                                  }}
                                  placeholder="Choice text" />
                                <label className="qb-choice-correct">
                                  <input
                                    type={child.questionCategory === CAT.MCQ ? 'checkbox' : 'radio'}
                                    name={`child-${ci}-correct`}
                                    checked={!!choice.correctFlag}
                                    onChange={() => {
                                      setChildQuestions(prev => prev.map((q, i) => {
                                        if (i !== ci) return q;
                                        const isMcq = q.questionCategory === CAT.MCQ;
                                        const newChoices = (q.choices || []).map((c, j) => ({
                                          ...c,
                                          correctFlag: isMcq ? (j === idx ? (c.correctFlag ? 0 : 1) : c.correctFlag) : (j === idx ? 1 : 0),
                                        }));
                                        return { ...q, choices: newChoices };
                                      }));
                                    }}
                                  />
                                  <span className={choice.correctFlag ? 'qb-correct-on' : ''}>Correct</span>
                                </label>
                              </div>
                            ))}
                          </div>
                          {child.questionCategory !== CAT.TRUE_FALSE && (
                            <Button variant="secondary" size="sm" style={{ marginTop: 6, marginRight: 2, marginLeft: 2 }}
                              onClick={() => {
                                const letters = 'ABCDEFGHIJ';
                                setChildQuestions(prev => prev.map((q, i) => {
                                  if (i !== ci) return q;
                                  const existing = (q.choices || []).filter(c => c.choiceGroup === 1);
                                  return { ...q, choices: [...(q.choices || []), { name: '', choiceString: letters[existing.length] || '', correctFlag: 0, choiceGroup: 1 }] };
                                }));
                              }}>+ Add Choice</Button>
                          )}
                        </div>
                      )}

                      {/* Child: Answer for text/fraction/integer types */}
                      {(child.questionCategory === CAT.TEXT || child.questionCategory === CAT.FRACTION) && (
                        <div style={{ marginBottom: 10 }}>
                          <label className="qb-label">Answer</label>
                          <Form.Control size="sm" type="text" value={child.answer || ''}
                            onChange={e => setChildQuestions(prev => prev.map((q, i) => i === ci ? { ...q, answer: e.target.value } : q))}
                            placeholder="Answer text" />
                        </div>
                      )}

                      {/* Child: Integer Range */}
                      {child.questionCategory === CAT.INTEGER_RANGE && (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                          <Form.Control size="sm" type="number" style={{ maxWidth: 150 }}
                            value={child.answerRangeStart ?? ''}
                            onChange={e => setChildQuestions(prev => prev.map((q, i) => i === ci ? { ...q, answerRangeStart: Number(e.target.value) } : q))}
                            placeholder="Range start" />
                          <span style={{ fontWeight: 600 }}>to</span>
                          <Form.Control size="sm" type="number" style={{ maxWidth: 150 }}
                            value={child.answerRangeEnd ?? ''}
                            onChange={e => setChildQuestions(prev => prev.map((q, i) => i === ci ? { ...q, answerRangeEnd: Number(e.target.value) } : q))}
                            placeholder="Range end" />
                        </div>
                      )}
                    </div>
                  ))}

                  <Button variant="outline-primary" size="sm" style={{ margin: '2px' }}
                    onClick={() => setChildQuestions(prev => [...prev, { name: '', stemStatement: '', mark: 0, questionCategory: null, choices: defaultChoicesForCategory(null), tagIds: [] }])}>
                    + Add Question
                  </Button>
                </div>
              )}

              {/* ---- Single question form (hidden during context-based create) ---- */}
              {(!isContextBased || editingId) && (<>

              {/* ---- Category-specific: SCQ choices (radio for correct) ---- */}
              {formData.questionCategory === CAT.SCQ && (
                <div className="qb-form-section qb-edit-choices">
                  <label className="qb-label">Choices <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>(Single Correct — click radio to mark correct)</span></label>
                  <div className="qb-choices-list">
                    {(formData.choices || []).filter(c => c.choiceGroup === 1).map((choice, idx) => (
                      <div className="qb-choice-row" key={idx}>
                        <span className="qb-choice-letter">{choice.choiceString}</span>
                        <div className="qb-choice-text">
                          <CKEditor
                            editor={ClassicEditor}
                            data={choice.name || ''}
                            onChange={(_event: any, editor: any) => updateChoice(idx, 'name', editor.getData())}
                            config={{ ...CKEDITOR_CONFIG_COMPACT, placeholder: 'Choice text' }}
                          />
                          <MathPreview html={choice.name || ''} />
                        </div>
                        <label className="qb-choice-correct">
                          <input
                            type="radio"
                            name="scq-correct"
                            checked={!!choice.correctFlag}
                            onChange={() => {
                              setFormData(prev => ({
                                ...prev,
                                choices: (prev.choices || []).map((c, i) => ({ ...c, correctFlag: i === idx ? 1 : 0 })),
                              }));
                            }}
                          />
                          <span className={choice.correctFlag ? 'qb-correct-on' : ''}>Correct</span>
                        </label>
                        <button className="qb-choice-remove" onClick={() => removeChoice(idx)} title="Remove choice">&times;</button>
                      </div>
                    ))}
                  </div>
                  <Button variant="secondary" size="sm" onClick={addChoice} style={{ marginTop: 8, marginRight: 2, marginLeft: 2 }}>+ Add Choice</Button>
                </div>
              )}

              {/* ---- Category-specific: MCQ choices (checkboxes for correct) ---- */}
              {formData.questionCategory === CAT.MCQ && (
                <div className="qb-form-section qb-edit-choices">
                  <label className="qb-label">Choices <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>(Multiple Correct — tick all correct answers)</span></label>
                  <div className="qb-choices-list">
                    {(formData.choices || []).filter(c => c.choiceGroup === 1).map((choice, idx) => (
                      <div className="qb-choice-row" key={idx}>
                        <span className="qb-choice-letter">{choice.choiceString}</span>
                        <div className="qb-choice-text">
                          <CKEditor
                            editor={ClassicEditor}
                            data={choice.name || ''}
                            onChange={(_event: any, editor: any) => updateChoice(idx, 'name', editor.getData())}
                            config={{ ...CKEDITOR_CONFIG_COMPACT, placeholder: 'Choice text' }}
                          />
                          <MathPreview html={choice.name || ''} />
                        </div>
                        <label className="qb-choice-correct">
                          <input
                            type="checkbox"
                            checked={!!choice.correctFlag}
                            onChange={e => updateChoice(idx, 'correctFlag', e.target.checked ? 1 : 0)}
                          />
                          <span className={choice.correctFlag ? 'qb-correct-on' : ''}>Correct</span>
                        </label>
                        <button className="qb-choice-remove" onClick={() => removeChoice(idx)} title="Remove choice">&times;</button>
                      </div>
                    ))}
                  </div>
                  <Button variant="secondary" size="sm" onClick={addChoice} style={{ marginTop: 8, marginRight: 2, marginLeft: 2 }}>+ Add Choice</Button>
                </div>
              )}

              {/* ---- Category-specific: True/False ---- */}
              {formData.questionCategory === CAT.TRUE_FALSE && (
                <div className="qb-form-section qb-edit-choices">
                  <label className="qb-label">Options <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>(Select the correct one)</span></label>
                  <div className="qb-choices-list">
                    {(formData.choices || []).filter(c => c.choiceGroup === 1).map((choice, idx) => (
                      <div className="qb-choice-row" key={idx}>
                        <span className="qb-choice-letter">{choice.choiceString}</span>
                        <span style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{choice.name}</span>
                        <label className="qb-choice-correct">
                          <input
                            type="radio"
                            name="tf-correct"
                            checked={!!choice.correctFlag}
                            onChange={() => {
                              setFormData(prev => ({
                                ...prev,
                                choices: (prev.choices || []).map((c, i) => ({ ...c, correctFlag: i === idx ? 1 : 0 })),
                              }));
                            }}
                          />
                          <span className={choice.correctFlag ? 'qb-correct-on' : ''}>Correct</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ---- Category-specific: Match the Following ---- */}
              {formData.questionCategory === CAT.MTF && (
                <div className="qb-form-section qb-edit-choices">
                  <label className="qb-label">Match the Following Pairs</label>
                  <Table size="sm" bordered className="qb-mtf-edit-table">
                    <thead>
                      <tr>
                        <th style={{ width: 30 }}>#</th>
                        <th>Left Item</th>
                        <th>Right Item</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {getMtfPairs().map((pair, i) => (
                        <tr key={i}>
                          <td style={{ textAlign: 'center', fontWeight: 600 }}>{i + 1}</td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={pair.left.name}
                              onChange={e => updateMtfItem(1, i, e.target.value)}
                              placeholder={`Left ${pair.left.choiceString}`}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={pair.right?.name || ''}
                              onChange={e => updateMtfItem(2, i, e.target.value)}
                              placeholder={`Right ${pair.right?.choiceString || ''}`}
                            />
                          </td>
                          <td>
                            <button className="qb-choice-remove" onClick={() => removeMtfPair(i)} title="Remove pair">&times;</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Button variant="secondary" size="sm" onClick={addMtfPair} style={{ marginTop: 4, marginRight: 2, marginLeft: 2 }}>+ Add Pair</Button>
                </div>
              )}

              {/* ---- Category-specific: Fill in the Blanks ---- */}
              {formData.questionCategory === CAT.FILL_BLANKS && (
                <div className="qb-form-section qb-edit-choices">
                  <label className="qb-label">Blank Answers</label>
                  <div className="qb-choices-list">
                    {getBlanks().map((blank, idx) => (
                      <div className="qb-choice-row" key={idx}>
                        <span className="qb-choice-letter" style={{ minWidth: 50 }}>Blank {idx + 1}</span>
                        <input
                          className="form-control form-control-sm qb-choice-text"
                          value={blank.name}
                          onChange={e => updateBlank(idx, e.target.value)}
                          placeholder={`Answer for blank ${idx + 1}`}
                        />
                        <button className="qb-choice-remove" onClick={() => removeBlank(idx)} title="Remove blank">&times;</button>
                      </div>
                    ))}
                  </div>
                  <Button variant="secondary" size="sm" onClick={addBlank} style={{ marginTop: 8, marginRight: 2, marginLeft: 2 }}>+ Add Blank</Button>
                </div>
              )}

              {/* ---- Category-specific: Integer Range ---- */}
              {formData.questionCategory === CAT.INTEGER_RANGE && (
                <div className="qb-form-section qb-edit-answer">
                  <label className="qb-label">Answer Range</label>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Form.Control
                      type="number"
                      size="sm"
                      style={{ maxWidth: 180 }}
                      value={formData.answerRangeStart ?? ''}
                      onChange={e => setFormData(prev => ({ ...prev, answerRangeStart: e.target.value ? Number(e.target.value) : null }))}
                      placeholder="Range start"
                    />
                    <span style={{ fontWeight: 600 }}>to</span>
                    <Form.Control
                      type="number"
                      size="sm"
                      style={{ maxWidth: 180 }}
                      value={formData.answerRangeEnd ?? ''}
                      onChange={e => setFormData(prev => ({ ...prev, answerRangeEnd: e.target.value ? Number(e.target.value) : null }))}
                      placeholder="Range end"
                    />
                  </div>
                </div>
              )}

              {/* ---- Category-specific: Fraction — answer field ---- */}
              {formData.questionCategory === CAT.FRACTION && (
                <div className="qb-form-section qb-edit-answer">
                  <label className="qb-label">Answer (Decimal / Fraction)</label>
                  <Form.Control
                    type="text"
                    size="sm"
                    style={{ maxWidth: 240 }}
                    value={formData.answer || ''}
                    onChange={e => setFormData(prev => ({ ...prev, answer: e.target.value }))}
                    placeholder="e.g. 3.14 or 22/7"
                  />
                </div>
              )}

              {/* ---- Category-specific: Text / Subjective — answer textarea ---- */}
              {formData.questionCategory === CAT.TEXT && (
                <div className="qb-form-section qb-edit-answer">
                  <label className="qb-label">Model Answer</label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    size="sm"
                    value={formData.answer || ''}
                    onChange={e => setFormData(prev => ({ ...prev, answer: e.target.value }))}
                    placeholder="Expected answer text"
                  />
                </div>
              )}

              {/* ---- Generic choices fallback (when no category is selected) ---- */}
              {formData.questionCategory === null && (
                <div className="qb-form-section qb-edit-choices">
                  <label className="qb-label">Choices</label>
                  <div className="qb-choices-list">
                    {(formData.choices || []).map((choice, idx) => (
                      <div className="qb-choice-row" key={idx}>
                        <span className="qb-choice-letter">{choice.choiceString}</span>
                        <div className="qb-choice-text">
                          <CKEditor
                            editor={ClassicEditor}
                            data={choice.name || ''}
                            onChange={(_event: any, editor: any) => updateChoice(idx, 'name', editor.getData())}
                            config={{ ...CKEDITOR_CONFIG_COMPACT, placeholder: 'Choice text' }}
                          />
                          <MathPreview html={choice.name || ''} />
                        </div>
                        <label className="qb-choice-correct">
                          <input
                            type="checkbox"
                            checked={!!choice.correctFlag}
                            onChange={e => updateChoice(idx, 'correctFlag', e.target.checked ? 1 : 0)}
                          />
                          <span className={choice.correctFlag ? 'qb-correct-on' : ''}>Correct</span>
                        </label>
                        <button className="qb-choice-remove" onClick={() => removeChoice(idx)} title="Remove choice">&times;</button>
                      </div>
                    ))}
                  </div>
                  <Button variant="secondary" size="sm" onClick={addChoice} style={{ marginTop: 8, marginRight: 2, marginLeft: 2 }}>+ Add Choice</Button>
                </div>
              )}

              {/* ---- GRAPH category: interactive graph authoring canvas ---- */}
              {formData.questionCategory === CAT.GRAPH && graphConfig && (
                <div className="qb-form-section" style={{ borderLeft: '4px solid var(--brand-accent)' }}>
                  <label className="qb-label" style={{ color: 'var(--brand-accent)' }}>Graph Configuration</label>
                  <GraphAuthoringCanvas config={graphConfig} onChange={setGraphConfig} />
                </div>
              )}

              {/* Explanation — CKEditor with equation tools (maps to OverallFeedBack column) */}
              <div className="qb-form-section qb-edit-explanation">
                <label className="qb-label">Explanation</label>
                <CKEditorWithTools
                  editorKey={`q-expl-${editingId || 'new'}-${loadingDetail}`}
                  data={formData.overallFeedback || ''}
                  onChange={(data: string) => {
                    setFormData(prev => ({ ...prev, overallFeedback: data }));
                  }}
                  config={CKEDITOR_CONFIG_COMPACT}
                  placeholder="Explanation for the correct answer"
                />
              </div>

              {/* Answer + Hint (for types that use a generic answer field) */}
              <div className="qb-meta-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                {formData.questionCategory !== CAT.FRACTION && formData.questionCategory !== CAT.TEXT && formData.questionCategory !== CAT.INTEGER_RANGE && (
                  <div>
                    <label className="qb-label">Answer</label>
                    <Form.Control
                      type="text"
                      value={formData.answer || ''}
                      onChange={e => setFormData(prev => ({ ...prev, answer: e.target.value }))}
                      placeholder="Answer text"
                      size="sm"
                    />
                  </div>
                )}
                <div>
                  <label className="qb-label">Hint</label>
                  <Form.Control
                    type="text"
                    value={formData.hint || ''}
                    onChange={e => setFormData(prev => ({ ...prev, hint: e.target.value }))}
                    placeholder="Optional hint"
                    size="sm"
                  />
                </div>
                <div>
                  <label className="qb-label">Solution Link</label>
                  <Form.Control
                    type="text"
                    value={formData.solutionLink || ''}
                    onChange={e => setFormData(prev => ({ ...prev, solutionLink: e.target.value }))}
                    placeholder="URL to solution video/doc"
                    size="sm"
                  />
                </div>
              </div>

              </>)}
              {/* End of single-question conditional wrapper */}

              {/* Tags section */}
              {lookups && lookups.tagTypes && lookups.tagTypes.length > 0 && (
                <div className="qb-form-section qb-edit-tags">
                  <label className="qb-label" style={{ fontSize: 13, marginBottom: 12 }}>Question Tags</label>

                  {/* Existing tags — removable pills (edit mode only) */}
                  {existingTags.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Current Tags</div>
                      {(() => {
                        const grouped: Record<string, typeof existingTags> = {};
                        existingTags.forEach(t => {
                          const key = t.tagTypeName || 'Other';
                          if (!grouped[key]) grouped[key] = [];
                          grouped[key].push(t);
                        });
                        return Object.entries(grouped).map(([typeName, tags]) => (
                          <div key={typeName} style={{ marginBottom: 6 }}>
                            <span style={{ fontSize: 11, color: 'var(--text-faint)', marginRight: 8, fontWeight: 500 }}>{typeName}:</span>
                            {tags.map(t => (
                              <span key={t.mappingId} className="qb-edit-tag-pill" style={{ background: TAG_TYPE_COLORS[t.tagTypeId] || '#e3f2fd' }}>
                                {t.tagName}
                                <button
                                  className="qb-edit-tag-remove"
                                  onClick={() => setExistingTags(prev => prev.filter(et => et.mappingId !== t.mappingId))}
                                  title="Remove tag"
                                >&times;</button>
                              </span>
                            ))}
                          </div>
                        ));
                      })()}
                    </div>
                  )}

                  {/* Add new tags — QuestionTagSelector */}
                  <div style={{ background: '#f8f9ff', border: '1px solid #e0e4f0', borderRadius: 8, padding: 14 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Add Tags</div>
                    <QuestionTagSelector
                      tagTypes={lookups.tagTypes}
                      tagSelections={tagSelections}
                      onTagSelectionsChange={setTagSelections}
                      addedTags={editAddedTags}
                      onAddedTagsChange={setEditAddedTags}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeFormModal}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ Delete Confirmation Modal ============ */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete question #{deletingId}? This will soft-delete it (it can be recovered).
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={saving}>
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ AI Generate Config Modal ============ */}
      <Modal show={showAiModal} onHide={() => setShowAiModal(false)} size="lg" dialogClassName="qb-ai-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>AI Generate Questions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {aiError && (
            <div style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-text)', padding: '8px 14px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>
              {aiError}
            </div>
          )}

          {lookups ? (
            <div className="qb-form">
              <div className="qb-meta-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div>
                  <label className="qb-label">Topic <span className="text-danger">*</span></label>
                  <Form.Select size="sm" value={aiParams.topicId || ''} onChange={e => setAiParams(prev => ({ ...prev, topicId: Number(e.target.value) }))}>
                    <option value="">— Select Topic —</option>
                    {lookups.topics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </Form.Select>
                </div>
                <div>
                  <label className="qb-label">Number of Questions</label>
                  <Form.Select size="sm" value={aiParams.count} onChange={e => setAiParams(prev => ({ ...prev, count: Number(e.target.value) }))}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n}</option>)}
                  </Form.Select>
                </div>
              </div>

              <div className="qb-meta-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', marginTop: 14 }}>
                <div>
                  <label className="qb-label">Category</label>
                  <Form.Select size="sm" value={aiParams.questionCategoryId || ''} onChange={e => setAiParams(prev => ({ ...prev, questionCategoryId: e.target.value ? Number(e.target.value) : undefined }))}>
                    <option value="">— None —</option>
                    {lookups.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Form.Select>
                </div>
                <div>
                  <label className="qb-label">Type</label>
                  <Form.Select size="sm" value={aiParams.questionTypeId} onChange={e => setAiParams(prev => ({ ...prev, questionTypeId: Number(e.target.value) }))}>
                    {lookups.types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </Form.Select>
                </div>
                <div>
                  <label className="qb-label">Difficulty</label>
                  <Form.Select size="sm" value={aiParams.difficultyLevelId} onChange={e => setAiParams(prev => ({ ...prev, difficultyLevelId: Number(e.target.value) }))}>
                    {lookups.difficulties.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Form.Select>
                </div>
              </div>

              <div className="qb-meta-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 14 }}>
                <div>
                  <label className="qb-label">Competency (optional)</label>
                  <Form.Select size="sm" value={aiParams.competencyTagId || ''} onChange={e => setAiParams(prev => ({ ...prev, competencyTagId: e.target.value ? Number(e.target.value) : undefined }))}>
                    <option value="">— None —</option>
                    {lookups.tags.filter(t => t.typeId === 12).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </Form.Select>
                </div>
                <div>
                  <label className="qb-label">Marks per Question</label>
                  <Form.Control type="number" size="sm" min={0} value={aiParams.marks || ''} onChange={e => setAiParams(prev => ({ ...prev, marks: Number(e.target.value) }))} />
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <label className="qb-label">Additional Instructions (optional)</label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  size="sm"
                  value={aiParams.instructions || ''}
                  onChange={e => setAiParams(prev => ({ ...prev, instructions: e.target.value }))}
                  placeholder="e.g. Focus on photosynthesis, include real-world examples, avoid numerical problems..."
                />
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40 }}>Loading options...</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAiModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAiGenerate} disabled={generating || !aiParams.topicId}>
            {generating ? <><Spinner role="status" animation="border" size="sm" /> Generating...</> : 'Generate Questions'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ AI Review/Edit Modal ============ */}
      <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)} size="lg" dialogClassName="qb-review-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>
            Review Generated Questions ({selectedIndices.size} of {generatedQuestions.length} selected)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {aiError && (
            <div style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-text)', padding: '8px 14px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>
              {aiError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <button
              className="action-btn"
              onClick={() => setSelectedIndices(new Set(generatedQuestions.map((_, i) => i)))}
            >Select All</button>
            <button
              className="action-btn"
              onClick={() => setSelectedIndices(new Set())}
            >Deselect All</button>
          </div>

          {generatedQuestions.map((q, idx) => (
            <div key={idx} className="qb-review-item">
              <div className="qb-review-header" onClick={() => setExpandedIndex(expandedIndex === idx ? null : idx)}>
                <input
                  type="checkbox"
                  checked={selectedIndices.has(idx)}
                  onChange={(e) => {
                    e.stopPropagation();
                    const next = new Set(selectedIndices);
                    if (next.has(idx)) next.delete(idx); else next.add(idx);
                    setSelectedIndices(next);
                  }}
                />
                <span className="qb-review-num">Q{idx + 1}</span>
                <span className="qb-review-stem">{stripHtml(q.stemStatement).substring(0, 120)}{stripHtml(q.stemStatement).length > 120 ? '...' : ''}</span>
                <span className="qb-review-toggle">{expandedIndex === idx ? '\u25BE' : '\u25B8'}</span>
              </div>

              {expandedIndex === idx && (
                <div className="qb-review-detail">
                  <label className="qb-label">Question Text</label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    size="sm"
                    value={q.stemStatement || ''}
                    onChange={e => updateGeneratedQuestion(idx, 'stemStatement', e.target.value)}
                  />

                  {q.choices && q.choices.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <label className="qb-label">Choices</label>
                      <div className="qb-choices-list">
                        {q.choices.map((c, ci) => (
                          <div className="qb-choice-row" key={ci}>
                            <span className="qb-choice-letter">{c.choiceString}</span>
                            <input
                              className="form-control form-control-sm qb-choice-text"
                              value={c.name}
                              onChange={e => updateGeneratedChoice(idx, ci, 'name', e.target.value)}
                            />
                            <label className="qb-choice-correct">
                              <input
                                type="checkbox"
                                checked={!!c.correctFlag}
                                onChange={e => updateGeneratedChoice(idx, ci, 'correctFlag', e.target.checked ? 1 : 0)}
                              />
                              <span className={c.correctFlag ? 'qb-correct-on' : ''}>Correct</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {q.answer && (
                    <div style={{ marginTop: 12 }}>
                      <label className="qb-label">Answer</label>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={q.answer || ''}
                        onChange={e => updateGeneratedQuestion(idx, 'answer', e.target.value)}
                      />
                    </div>
                  )}

                  <div style={{ marginTop: 12 }}>
                    <label className="qb-label">Explanation</label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      size="sm"
                      value={q.explanation || ''}
                      onChange={e => updateGeneratedQuestion(idx, 'explanation', e.target.value)}
                    />
                  </div>

                  <div className="qb-meta-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 12 }}>
                    <div>
                      <label className="qb-label">Hint</label>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={q.hint || ''}
                        onChange={e => updateGeneratedQuestion(idx, 'hint', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="qb-label">Marks</label>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={q.mark || ''}
                        onChange={e => updateGeneratedQuestion(idx, 'mark', Number(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReviewModal(false)}>Cancel</Button>
          <Button variant="secondary" onClick={() => { setShowReviewModal(false); setAiError(''); setShowAiModal(true); }}>
            Regenerate
          </Button>
          <Button variant="primary" onClick={handleSaveGenerated} disabled={savingGenerated || selectedIndices.size === 0}>
            {savingGenerated
              ? `Saving (${saveProgress.saved}/${saveProgress.total})...`
              : `Save ${selectedIndices.size} Question${selectedIndices.size !== 1 ? 's' : ''}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QuestionBankPage;
